import React, {useCallback, useEffect, useState} from 'react'
import {Box, Typography} from '@mui/material'
import moment from 'moment'
import DataTable, {ColumnDef, PaginationState} from 'src/components-new/data/DataTable'
import Filters, {FilterItem, FilterResult} from 'src/components-new/data/Filters'
import FilePreviewDialog from 'src/components-new/feedback/FilePreviewDialog'
import InlineMessage from 'src/components-new/feedback/InlineMessage'
import ActionIconButton from 'src/components-new/primitives/ActionIconButton'
import ExportFilesDialogNew, {ExportFileType} from 'src/pages-new/patient/tabs/pharmacovigilance/ExportFilesDialogNew'
import ConfirmGenerateFormDialogNew from 'src/pages-new/patient/tabs/pharmacovigilance/ConfirmGenerateFormDialogNew'
import {PREVIEW_MIME_TYPES, STATUS} from 'src/constants/constants'
import {useAppSelector} from 'src/hooks'
import apiClient from 'src/services/apiClient'

interface PharmacovigilanceTabProps {
  patientId: string
}

interface PharmacovigilanceRow {
  _id: string
  patientId: string
  eventDate?: string
  code?: string
  eventType?: string
  state?: string
  signallingDate?: string
  reportDate?: string
  notificationDate?: string
  reportType?: string
  patientFirstName?: string
  patientLastName?: string
  exportFiles?: ExportFileType[]
  [key: string]: any
}

interface PharmacovigilanceColumnConfig {
  id: string
  label: string
}

const DEFAULT_COLUMNS: PharmacovigilanceColumnConfig[] = [
  {id: 'eventDate', label: 'DATA EVENTO'},
  {id: 'code', label: 'CODICE EVENTO'},
  {id: 'eventType', label: 'TIPO'},
  {id: 'reportType', label: 'TIPO SEGNALAZIONE'},
  {id: 'reportDate', label: 'DATA RACCOLTA SEGNALAZIONE'},
  {id: 'notificationDate', label: 'DATA INVIO'},
]

const EVENT_TYPE_OPTIONS = [
  {value: 'segnalazioneIniziale', label: 'Segnalazione iniziale'},
  {value: 'segnalazioneFollowUp', label: 'Segnalazione follow up'},
]

const REPORT_TYPE_OPTIONS = [
  {value: 'icrs', label: 'ICRS'},
  {value: 'mi', label: 'MI'},
  {value: 'tpc', label: 'TPC'},
  {value: 'pc', label: 'PC'},
  {value: 'deviazioneDallaSchedula', label: 'Deviazione dalla schedula'},
]

// Filters that mirror a table column are keyed by the column's id(s) here.
// The 'signallingDate' filter predates the 'reportDate' column (which reads
// the real Mongo field instead of the psp-backend 'signallingDate' alias for
// createdAt) so it's mapped to both, staying compatible with any PSP config
// that still lists the column under the old id. Filters absent from this map
// have no corresponding column and are always shown, regardless of column
// config.
const FILTER_TO_COLUMN_IDS: Record<string, string[]> = {
  eventDate: ['eventDate'],
  eventType: ['eventType'],
  signallingDate: ['reportDate', 'signallingDate'],
  notificationDate: ['notificationDate'],
  code: ['code'],
  reportType: ['reportType'],
}

const FILTER_CONFIG: FilterItem[] = [
  {id: 'eventDate', label: 'Data evento', type: 'rangePicker'},
  {
    id: 'eventType',
    label: 'Tipo evento',
    type: 'select',
    options: EVENT_TYPE_OPTIONS,
  },
  {id: 'signallingDate', label: 'Data raccolta segnalazione', type: 'rangePicker'},
  {id: 'notificationDate', label: 'Data notifica al Cliente', type: 'rangePicker'},
  {id: 'code', label: 'Codice', type: 'text'},
  {
    id: 'reportType',
    label: 'Tipo segnalazione',
    type: 'select',
    options: REPORT_TYPE_OPTIONS,
  },
]

const INITIAL_VIEWER_STATE = {open: false, filePath: '', mime: '', fileName: '', status: STATUS.PENDING}

function renderCell(id: string) {
  return (row: PharmacovigilanceRow): React.ReactNode => {
    if (id === 'eventDate' || id === 'notificationDate' || id === 'reportDate' || id === 'signallingDate') {
      return <Typography variant="body2">{row[id] ? moment(row[id]).format('DD MMMM YYYY') : '—'}</Typography>
    }
    if (id === 'eventType') {
      const label = EVENT_TYPE_OPTIONS.find(opt => opt.value === row[id])?.label
      return <Typography variant="body2">{label ?? row[id] ?? '—'}</Typography>
    }
    if (id === 'reportType') {
      const label = REPORT_TYPE_OPTIONS.find(opt => opt.value === row[id])?.label
      return <Typography variant="body2">{label ?? row[id] ?? '—'}</Typography>
    }
    return <Typography variant="body2">{row[id] ?? '—'}</Typography>
  }
}

function forceDownload(url: string, downloadName: string) {
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = downloadName
  document.body.appendChild(anchor)
  anchor.click()
  document.body.removeChild(anchor)
  URL.revokeObjectURL(url)
}

/**
 * Each event row may expose several generable form templates ("exportFiles").
 * A single file downloads immediately, no confirmation needed. Multiple files
 * open a picker dialog to choose one, then a confirm dialog before generating
 * — since there the user is picking among options, a confirmation step makes
 * sense. The download endpoint returns {data, headers} — like DossierTab,
 * whether the result is previewed or force-downloaded is decided from the
 * response's content-type.
 */
export default function PharmacovigilanceTab({patientId}: PharmacovigilanceTabProps): JSX.Element {
  const pharmacovigilanceColumnOptions: PharmacovigilanceColumnConfig[] = useAppSelector(
    state => state.pspConfigReducer.data.patientDetailConfiguration?.pharmacovigilanceConfiguration?.columnOptions
  )

  const [rows, setRows] = useState<PharmacovigilanceRow[]>([])
  const [count, setCount] = useState<number>(0)
  const [status, setStatus] = useState<string>(STATUS.PENDING)
  const [statusCount, setStatusCount] = useState<string>(STATUS.PENDING)
  const [downloadStatus, setDownloadStatus] = useState<string | undefined>()
  const [pagination, setPagination] = useState<PaginationState>({page: 0, rowsPerPage: 10, order: ''})
  const [filters, setFilters] = useState<FilterResult[]>([])
  const [viewerState, setViewerState] = useState(INITIAL_VIEWER_STATE)

  const [selectedRow, setSelectedRow] = useState<PharmacovigilanceRow>()
  const [selectedExportIndex, setSelectedExportIndex] = useState<number>(0)
  const [filePickerOpen, setFilePickerOpen] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)

  const fetchData = useCallback(() => {
    Promise.all([
      apiClient.getPharmacovigilanceEvents(patientId, filters, setStatus, pagination),
      apiClient.getPharmacovigilanceEventsCount(patientId, filters, setStatusCount),
    ])
      .then(([events, countResult]) => {
        if (Array.isArray(events)) {
          setRows(events)
        }
        const n = Number(countResult)
        if (!isNaN(n)) setCount(n)
      })
      .catch(() => setStatus(STATUS.REJECTED))
  }, [patientId, filters, pagination])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  // On download failure, close both the confirm dialog and the file-picker
  // dialog so the page-level InlineMessage below becomes visible again.
  useEffect(() => {
    if (downloadStatus === STATUS.REJECTED) {
      setConfirmOpen(false)
      setFilePickerOpen(false)
      setSelectedRow(undefined)
    }
  }, [downloadStatus])

  const handleFiltersSubmit = (values: FilterResult[]) => {
    setFilters(values)
    setPagination(prev => ({...prev, page: 0}))
  }

  const generateForm = async (file?: ExportFileType) => {
    if (!file) return

    const fileResponse = await apiClient.downloadPharmacovigilance(file.fileId, setDownloadStatus)
    if (!fileResponse) return

    setConfirmOpen(false)
    setFilePickerOpen(false)
    setSelectedRow(undefined)

    const mime = fileResponse.headers['content-type']
    const blob = new Blob([new Uint8Array(fileResponse.data)], {type: mime})
    const url = (window.URL || window.webkitURL).createObjectURL(blob)

    if (PREVIEW_MIME_TYPES.includes(mime)) {
      setViewerState({open: true, filePath: url, mime, fileName: file.fileName, status: STATUS.FULFILLED})
      return
    }
    forceDownload(url, file.fileName)
  }

  const handleActionClick = (row: PharmacovigilanceRow) => {
    const exportFiles = row.exportFiles ?? []
    if (exportFiles.length === 1) {
      generateForm(exportFiles[0])
    } else {
      setSelectedRow(row)
      setFilePickerOpen(true)
    }
  }

  const handlePickFile = (index: number) => {
    setSelectedExportIndex(index)
    setConfirmOpen(true)
  }

  const handleConfirmClose = () => {
    setConfirmOpen(false)
    // Leave the file picker open so the user can go back and pick a different file.
  }

  const handleFilePickerClose = () => {
    setFilePickerOpen(false)
    setSelectedRow(undefined)
  }

  const handleConfirmGenerate = () => {
    const exportFiles = selectedRow?.exportFiles ?? []
    return generateForm(exportFiles[selectedExportIndex])
  }

  const columnConfig = pharmacovigilanceColumnOptions?.length > 0 ? pharmacovigilanceColumnOptions : DEFAULT_COLUMNS
  const visibleColumnIds = new Set(columnConfig.map(col => col.id))

  // Hide any filter whose linked column(s) have all been switched off via
  // psp config, so the filter list always matches what's actually visible.
  const filterConfig = FILTER_CONFIG.filter(item => {
    const linkedColumnIds = FILTER_TO_COLUMN_IDS[item.id]
    return !linkedColumnIds || linkedColumnIds.some(id => visibleColumnIds.has(id))
  })

  const columns: ColumnDef<PharmacovigilanceRow>[] = [
    // On mobile, the event code becomes the card's bold header (like a filename —
    // it's the one field that uniquely identifies the row) instead of just another
    // labeled field in the body grid.
    ...columnConfig.map(col => ({
      key: col.id,
      header: col.label,
      render: renderCell(col.id),
      mobileCardHeader: col.id === 'code',
    })),
    {
      key: 'actions',
      header: '',
      width: 56,
      isActionColumn: true,
      render: (row: PharmacovigilanceRow) => (
        (row.exportFiles?.length ?? 0) > 0 ? (
          <Box onClick={(e) => e.stopPropagation()}>
            <ActionIconButton variant="download" tooltip="Genera form" onClick={() => handleActionClick(row)} />
          </Box>
        ) : null
      ),
    },
  ]

  return (
    <Box>
      {(status === STATUS.REJECTED || statusCount === STATUS.REJECTED) && (
        <InlineMessage
          severity="error"
          onClose={() => { setStatus(STATUS.FULFILLED); setStatusCount(STATUS.FULFILLED) }}
          sx={{mb: 2}}
        >
          {'Errore nel caricare i dati.'}
        </InlineMessage>
      )}

      {downloadStatus === STATUS.REJECTED && (
        <InlineMessage severity="error" onClose={() => setDownloadStatus(undefined)} sx={{mb: 2}}>
          {'Errore nello scaricare documento.'}
        </InlineMessage>
      )}

      <Box sx={{display: 'flex', justifyContent: 'flex-end', mb: 2}}>
        <Filters config={filterConfig} onSubmit={handleFiltersSubmit} />
      </Box>

      <DataTable<PharmacovigilanceRow>
        columns={columns}
        rows={rows}
        keyField="_id"
        loading={status === STATUS.PENDING || statusCount === STATUS.PENDING}
        emptyMessage="Nessun evento trovato"
        totalElements={count}
        pagination={pagination}
        onPaginationChange={setPagination}
      />

      <ExportFilesDialogNew
        open={filePickerOpen}
        onClose={handleFilePickerClose}
        exportFiles={selectedRow?.exportFiles ?? []}
        onPick={handlePickFile}
      />

      <ConfirmGenerateFormDialogNew
        open={confirmOpen}
        onClose={handleConfirmClose}
        onConfirm={handleConfirmGenerate}
        downloading={downloadStatus === STATUS.PENDING}
      />

      <FilePreviewDialog
        fileName={viewerState.fileName}
        filePath={viewerState.filePath}
        mime={viewerState.mime}
        onClose={() => setViewerState(INITIAL_VIEWER_STATE)}
        open={viewerState.open}
        status={viewerState.status}
      />
    </Box>
  )
}
